import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const doRedirect = (result) => {
    const urlParams = new URLSearchParams(window.location.search);
    const returnTo = urlParams.get('returnTo') || '/';
    const publicPages = ['/', '/about', '/contact', '/pricing', '/services'];
    if (result.user.role === 'ADMIN' || result.user.role === 'SUPER_ADMIN') {
      navigate(publicPages.includes(returnTo) ? returnTo : '/admin');
    } else {
      const restricted = ['/admin'];
      navigate(restricted.some((p) => returnTo.startsWith(p)) ? '/dashboard' : returnTo);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await login(formData);
      if (result.success) { doRedirect(result); } else { setError(result.error || 'Login failed'); }
    } catch { setError('An unexpected error occurred'); } finally { setLoading(false); }
  };

  const quickLogin = async (username, password) => {
    setLoading(true);
    setError('');
    try {
      const result = await login({ username, password });
      if (result.success) { doRedirect(result); } else { setError(result.error || 'Login failed'); }
    } catch { setError('An unexpected error occurred'); } finally { setLoading(false); }
  };

  const features = [
    { icon: 'psychology', title: 'Neural Orchestration', desc: 'LLM-powered decision engines at enterprise scale' },
    { icon: 'hub', title: '500+ Integrations', desc: 'Connect your entire stack in minutes' },
    { icon: 'bolt', title: 'Sub-100ms Latency', desc: 'Real-time automation that never slows you down' },
  ];

  return (
    <div className="min-h-screen flex font-display" style={{ backgroundColor: '#101c22', color: '#fff' }}>
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(19,164,236,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(19,164,236,0.15) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="absolute top-1/4 left-1/4 w-80 h-80 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, #13a4ec 0%, transparent 70%)', filter: 'blur(80px)', opacity: 0.2 }} />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)', filter: 'blur(80px)', opacity: 0.15 }} />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-9 h-9" style={{ color: '#13a4ec' }}>
              <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="20" stroke="currentColor" strokeDasharray="10 4" strokeWidth="4" />
                <circle cx="24" cy="24" fill="currentColor" r="8" />
                <path d="M40 24C40 32.8366 32.8366 40 24 40" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
              </svg>
            </div>
            <span className="text-xl font-bold text-white">OrbitMind</span>
          </div>
          <h2 className="text-4xl font-extrabold text-white mb-3 leading-tight">The AI Brain Behind<br />Your Business</h2>
          <p className="text-gray-400 mb-12">Enterprise-grade AI orchestration for teams that move fast.</p>
          <div className="space-y-6">
            {features.map((f) => (
              <div key={f.icon} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}>
                  <span className="material-symbols-outlined text-white text-base">{f.icon}</span>
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">{f.title}</p>
                  <p className="text-gray-400 text-sm">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
          <p className="text-gray-300 text-sm italic mb-4">"OrbitMind cut our manual processing time by 80%. It's the backbone of our entire ops stack."</p>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}>SR</div>
            <div>
              <p className="text-white text-sm font-semibold">Sarah R.</p>
              <p className="text-gray-500 text-xs">VP of Operations, TechCorp</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-8 h-8" style={{ color: '#13a4ec' }}>
              <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="20" stroke="currentColor" strokeDasharray="10 4" strokeWidth="4" />
                <circle cx="24" cy="24" fill="currentColor" r="8" />
                <path d="M40 24C40 32.8366 32.8366 40 24 40" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
              </svg>
            </div>
            <span className="text-xl font-bold text-white">OrbitMind</span>
          </div>
          <h1 className="text-3xl font-extrabold text-white mb-1">Welcome back</h1>
          <p className="text-gray-400 text-sm mb-8">Sign in to your OrbitMind account</p>
          {error && <div className="mb-5 px-4 py-3 rounded-xl border border-red-500/20 bg-red-500/10 text-red-400 text-sm">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xl pointer-events-none">person</span>
                <input type="text" name="username" value={formData.username} onChange={handleChange} required disabled={loading} placeholder="Enter your username" className="w-full h-12 pl-10 pr-4 rounded-xl border border-white/10 bg-white/5 text-white placeholder-gray-500 outline-none focus:border-primary transition-colors text-sm" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-300">Password</label>
                <a href="#" className="text-xs font-medium hover:opacity-80 transition-opacity" style={{ color: '#13a4ec' }}>Forgot password?</a>
              </div>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xl pointer-events-none">lock</span>
                <input type="password" name="password" value={formData.password} onChange={handleChange} required disabled={loading} placeholder="••••••••" className="w-full h-12 pl-10 pr-4 rounded-xl border border-white/10 bg-white/5 text-white placeholder-gray-500 outline-none focus:border-primary transition-colors text-sm" />
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full h-12 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}>
              {loading ? (<><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span>Signing In...</span></>) : (<><span>Sign In</span><span className="material-symbols-outlined text-base">login</span></>)}
            </button>
          </form>
          <div className="relative flex items-center my-6">
            <div className="flex-1 border-t border-white/10" />
            <span className="mx-4 text-xs text-gray-500 uppercase tracking-widest">Or continue with</span>
            <div className="flex-1 border-t border-white/10" />
          </div>
          <div className="grid grid-cols-2 gap-3 mb-6">
            <button className="flex items-center justify-center gap-2 h-11 rounded-xl border border-white/10 bg-white/5 text-sm font-medium text-gray-300 hover:bg-white/10 hover:border-white/20 transition-all">
              <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M12.48 10.92v3.28h7.84c-.24 1.84-.90 3.16-1.92 4.18-1.28 1.28-3.3 2.68-6.92 2.68-6.04 0-11-4.96-11-11s4.96-11 11-11c3.24 0 5.6 1.28 7.36 2.92l2.32-2.32C18.44 1.2 15.68 0 12 0 5.38 0 0 5.38 0 12s5.38 12 12 12c3.56 0 6.44-1.2 8.6-3.48 2.24-2.24 2.92-5.4 2.92-7.84 0-.52-.04-1.04-.12-1.76H12.48z" fill="#EA4335" /></svg>
              Google
            </button>
            <button className="flex items-center justify-center gap-2 h-11 rounded-xl border border-white/10 bg-white/5 text-sm font-medium text-gray-300 hover:bg-white/10 hover:border-white/20 transition-all">
              <svg className="w-4 h-4" viewBox="0 0 23 23"><path d="M1 1h10v10H1z" fill="#f35325" /><path d="M12 1h10v10H12z" fill="#81bc06" /><path d="M1 12h10v10H1z" fill="#05a6f0" /><path d="M12 12h10v10H12z" fill="#ffba08" /></svg>
              Microsoft
            </button>
          </div>
          <div className="rounded-xl border border-white/10 p-4 mb-6" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
            <p className="text-xs text-gray-400 font-medium mb-3">Demo Credentials (click to login):</p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => quickLogin('demo', 'demo123')} disabled={loading} className="p-3 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-all disabled:opacity-50 text-left">
                <p className="text-xs text-gray-400">User: <span className="text-white font-mono">demo</span></p>
                <p className="text-xs text-gray-400">Pass: <span className="text-white font-mono">demo123</span></p>
              </button>
              <button onClick={() => quickLogin('admin', 'admin123')} disabled={loading} className="p-3 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-all disabled:opacity-50 text-left">
                <p className="text-xs text-gray-400">Admin: <span className="text-white font-mono">admin</span></p>
                <p className="text-xs text-gray-400">Pass: <span className="text-white font-mono">admin123</span></p>
              </button>
            </div>
          </div>
          <p className="text-center text-sm text-gray-400">
            Don't have an account?{' '}
            <Link to="/signup" className="font-bold hover:opacity-80 transition-opacity" style={{ color: '#13a4ec' }}>Create Account</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
